import { czyWidziDostawy } from '@/lib/auth/dostepZrodel';
import { keepPreviousData, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useServices } from '@/lib/ServicesContext';
import { useAuth } from '@/lib/auth/AuthContext';
import { createAnalyticsApi, type PowodNieswiezosci, type SwiezoscRozliczen, type FiltrHistoriiModeracji, type OpinieZapytanie, type ParytetZapytanie, type SalesQuery, type SchoolSalesRow, type TransactionQuery, type ZapytanieTransakcjiRestauracji, type ZapytanieRaportuDostaw, type ZapytanieUzytkownikow } from '@/lib/api/analytics';
import { createDriveApi, type DeliveryQuery } from '@/lib/api/drive';
import { assessJoin, findCoincidences, type SalesWindowRow } from '@/lib/crossSystem';
import { useDateRange, type DateRange } from '@/lib/useDateRange';

export function useAnalyticsApi() {
  const { api } = useServices();
  return useMemo(() => createAnalyticsApi(api), [api]);
}

export function useDriveApi() {
  const { api } = useServices();
  return useMemo(() => createDriveApi(api), [api]);
}

/** Wspólne ustawienia: nie ponawiamy błędów autoryzacji ani braku endpointu. */
const QUERY_OPTIONS = {
  staleTime: 60_000,
  retry: (failureCount: number, error: unknown) => {
    const kind = (error as { kind?: string })?.kind;
    if (kind === 'unauthorized' || kind === 'forbidden' || kind === 'not-found') return false;
    return failureCount < 2;
  },
} as const;

// ── Sprzedaż (CRM) ────────────────────────────────────────────────────────────

export function useSchoolSales(query: SalesQuery = {}) {
  const api = useAnalyticsApi();
  const [range] = useDateRange();
  const params = { ...range, ...query };
  return useQuery({ queryKey: ['school-sales', params], queryFn: () => api.schoolSales(params), ...QUERY_OPTIONS });
}

export function useRestaurantSales(query: SalesQuery = {}) {
  const api = useAnalyticsApi();
  const [range] = useDateRange();
  const params = { ...range, ...query };
  return useQuery({ queryKey: ['restaurant-sales', params], queryFn: () => api.restaurantSales(params), ...QUERY_OPTIONS });
}

export function useDishSales(query: SalesQuery = {}) {
  const api = useAnalyticsApi();
  const [range] = useDateRange();
  const params = { ...range, ...query };
  return useQuery({ queryKey: ['dish-sales', params], queryFn: () => api.dishSales(params), ...QUERY_OPTIONS });
}

// ── Dostawy (Drive) ───────────────────────────────────────────────────────────
// Zapytania Drive są WŁĄCZANE dopiero po podłączeniu sesji Drive. Bez tego lecielibyśmy
// w 401 przy każdym renderze i zaśmiecali ekran błędem, który nie jest błędem.

function useDriveEnabled() {
  const auth = useAuth();
  return czyWidziDostawy(auth);
}

export function useDeliverySummary(query: DeliveryQuery = {}) {
  const api = useDriveApi();
  const [range] = useDateRange();
  const enabled = useDriveEnabled();
  const params = { ...range, ...query };
  return useQuery({ queryKey: ['delivery-summary', params], queryFn: () => api.summary(params), enabled, ...QUERY_OPTIONS });
}

export function useDeliveryBySchool(query: DeliveryQuery = {}) {
  const api = useDriveApi();
  const [range] = useDateRange();
  const enabled = useDriveEnabled();
  const params = { ...range, ...query };
  return useQuery({ queryKey: ['delivery-by-school', params], queryFn: () => api.bySchool(params), enabled, ...QUERY_OPTIONS });
}

export function useDeliveryTrend(query: DeliveryQuery = {}) {
  const api = useDriveApi();
  const [range] = useDateRange();
  const enabled = useDriveEnabled();
  const params = { granularity: 'day' as const, ...range, ...query };
  return useQuery({ queryKey: ['delivery-trend', params], queryFn: () => api.trend(params), enabled, ...QUERY_OPTIONS });
}

export function useDeliveryHistory(query: DeliveryQuery = {}) {
  const api = useDriveApi();
  const [range] = useDateRange();
  const enabled = useDriveEnabled();
  const params = { limit: 100, ...range, ...query };
  return useQuery({ queryKey: ['delivery-history', params], queryFn: () => api.history(params), enabled, ...QUERY_OPTIONS });
}

export function useTuszamaIdCoverage(query: DeliveryQuery = {}) {
  const api = useDriveApi();
  const [range] = useDateRange();
  const enabled = useDriveEnabled();
  const params = { ...range, ...query };
  return useQuery({ queryKey: ['tuszama-coverage', params], queryFn: () => api.coverage(params), enabled, ...QUERY_OPTIONS });
}

// ── Analiza łączona ───────────────────────────────────────────────────────────

/** Poprzednie okno o TEJ SAMEJ długości — ta sama reguła co po stronie backendów. */
function previousWindow(range: DateRange): DateRange {
  const from = new Date(`${range.date_from}T00:00:00Z`);
  const to = new Date(`${range.date_to}T00:00:00Z`);
  const days = Math.floor((to.getTime() - from.getTime()) / 86400000) + 1;
  const prevTo = new Date(from.getTime() - 86400000);
  const prevFrom = new Date(prevTo.getTime() - (days - 1) * 86400000);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { date_from: iso(prevFrom), date_to: iso(prevTo) };
}

function toSalesWindow(rows: readonly SchoolSalesRow[]): SalesWindowRow[] {
  return rows.map((r) => ({
    school_id: r.school_id,
    school_name: r.school_name,
    city: r.city,
    sales_grosze: r.period_sales_grosze,
    orders: r.period_orders,
  }));
}

/**
 * Zbieżność sprzedaży i dostaw. Wymaga OBU sesji — bez podłączonego Drive nie ma z czym łączyć.
 * Zwraca też raport złączenia, żeby dało się odróżnić „brak sygnału” od „nie da się policzyć”.
 */
export function useSalesDeliveryCoincidence() {
  const api = useAnalyticsApi();
  const [range] = useDateRange();
  const previous = useMemo(() => previousWindow(range), [range]);
  const driveConnected = useDriveEnabled();

  const current = useSchoolSales();
  const bySchool = useDeliveryBySchool();
  const prior = useQuery({
    queryKey: ['school-sales', previous],
    queryFn: () => api.schoolSales(previous),
    enabled: driveConnected,
    ...QUERY_OPTIONS,
  });

  const result = useMemo(() => {
    if (!driveConnected) {
      // Po przejściu na jedno logowanie to NIE jest już „brak sesji Drive" — sesja jest jedna.
      // To brak UPRAWNIENIA do danych o dostawach, a bez nich nie ma czego z czym zestawiać.
      return { available: false as const, reason: 'Brak uprawnienia do danych o dostawach.' };
    }
    if (!current.data || !prior.data || !bySchool.data) return null;
    const salesCurrent = toSalesWindow(current.data.schools);
    const salesPrevious = toSalesWindow(prior.data.schools);
    const zbieznosci = findCoincidences(salesCurrent, salesPrevious, bySchool.data.schools);
    return {
      available: true as const,
      join: assessJoin(salesCurrent, bySchool.data.schools),
      coincidences: zbieznosci.coincidences,
      // POPULACJA jedzie razem z wynikiem, bo bez niej pusta lista jest nie do zinterpretowania:
      // „przeanalizowano i nic nie ma" i „nie było czego przeanalizować" wyglądają identycznie.
      population: zbieznosci.population,
      window: range,
      comparison: previous,
    };
  }, [driveConnected, current.data, prior.data, bySchool.data, range, previous]);

  return {
    data: result,
    isLoading: driveConnected && (current.isLoading || prior.isLoading || bySchool.isLoading),
    error: current.error ?? prior.error ?? bySchool.error,
  };
}

// ── O1: rejestr płatności i katalog dań ──────────────────────────────────────

export function useTransactions(query: TransactionQuery = {}) {
  const api = useAnalyticsApi();
  return useQuery({ queryKey: ['transactions', query], queryFn: () => api.transactions(query), ...QUERY_OPTIONS });
}

export function useTransactionDetail(id: number | null) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['transaction', id],
    queryFn: () => api.transactionDetail(id as number),
    // Bez identyfikatora nie ma czego pobierać — zapytanie wyłączone, a nie wołane z null-em.
    enabled: id !== null,
    ...QUERY_OPTIONS,
  });
}

export function useDishCatalog(query: { restaurant_id?: number; search?: string } = {}) {
  const api = useAnalyticsApi();
  return useQuery({ queryKey: ['dish-catalog', query], queryFn: () => api.dishCatalog(query), ...QUERY_OPTIONS });
}

/**
 * Słowniki referencyjne. Świadomie osobne hooki od `useSchoolSales` / `useRestaurantSales`:
 * tamte pytają „gdzie coś kupiono", te — „co w ogóle istnieje". Wspólny hook zachęcałby do
 * mieszania świeżości jednego ze świeżością drugiego, a to jest dokładnie ten błąd, którego
 * ten ekran ma nie popełniać.
 */
export function useSchoolsDictionary() {
  const api = useAnalyticsApi();
  return useQuery({ queryKey: ['schools-dictionary'], queryFn: () => api.schoolsDictionary(), ...QUERY_OPTIONS });
}

export function useRestaurantsDictionary() {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['restaurants-dictionary'],
    queryFn: () => api.restaurantsDictionary(),
    ...QUERY_OPTIONS,
  });
}

// ── PARYTET ZE STARYM PANELEM: ZIARNO POZYCJI ZAMÓWIENIA ─────────────────────

export function useTransakcjeParytet(q: ParytetZapytanie = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['transakcje-parytet', q],
    queryFn: () => api.transakcjeParytet(q),
    ...QUERY_OPTIONS,
  });
}

/** Listy wyboru filtrów — te same etykiety, którymi filtruje stary panel. */
export function useTransakcjeParytetSlowniki() {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['transakcje-parytet-slowniki'],
    queryFn: () => api.transakcjeParytetSlowniki(),
    ...QUERY_OPTIONS,
  });
}

// ── ADMINISTRACJA → ROZLICZENIA ──────────────────────────────────────────────

/**
 * Rozliczenie tygodnia. `staleTime: 0` celowo: serwer liczy wszystko od nowa przy każdym
 * odczycie, więc usunięcie transakcji (po synchronizacji) ma być widoczne przy powrocie na
 * ekran, a nie po minucie. `undefined` = tydzień domyślny wybrany przez serwer.
 */
/*
 * ODŚWIEŻANIE OTWARTYCH ROZLICZEŃ (PM 28.10): limit 60 s obejmuje całą drogę zapis w Main → synchronizacja →
 * agregaty → ekran. Stały interwał 60 s sam zużywał limit, a rok co 5 min go łamał. Teraz otwarty widok co
 * `znacznik` ms pyta o ODCISK swoich danych (`/znacznik`, jedno zapytanie agregujące tym samym fragmentem co obrót)
 * i przelicza się WYŁĄCZNIE, gdy odcisk różni się od `changeFingerprint` danych, które pokazuje — nie przy każdym
 * przebiegu synchronizacji (recenzja #985: `rows > 0` ≠ zmiana). Rok najwyżej co `rokMinOdstep`. Karta w tle nie
 * odpytuje (domyślne zachowanie zapytań); po powrocie odcisk czyta się od razu.
 */
export const ODSWIEZANIE_ROZLICZEN_MS = Object.freeze({ znacznik: 10_000, znacznikRok: 20_000, rokMinOdstep: 20_000 });

type ParametryZnacznika = Record<string, string | number>;

/** Po nieudanym ponownym odczycie widoku — najwyżej co tyle (bez pętli na wadliwym endpoincie, recenzja #985 r2). */
const ODSTEP_PO_BLEDZIE_MS = 30_000;

/**
 * Przelicza widok `klucz`, gdy odcisk danych widoku różni się od odcisku pokazanych danych. Zwraca ŚWIEŻOŚĆ NA ŻYWO
 * z ostatniego odczytu znacznika — panel świeżości nie zastyga, gdy dane stoją, a synchronizacja nie działa.
 */
function useOdswiezPoZmianieDanych(
  klucz: readonly unknown[], parametry: ParametryZnacznika | null, odciskDanych: string | undefined,
  pobiera: boolean, blad: boolean, danePobraneO: number, minOdstepMs = 0, coIleMs: number = ODSWIEZANIE_ROZLICZEN_MS.znacznik,
): { swiezosc: SwiezoscRozliczen | undefined; zgodne: boolean; znacznikBlad: boolean } {
  const api = useAnalyticsApi();
  const kolejka = useQueryClient();
  const znacznik = useQuery({
    queryKey: ['rozliczenia-znacznik', parametry],
    queryFn: () => api.rozliczeniaZnacznik(parametry as ParametryZnacznika),
    enabled: parametry !== null && odciskDanych !== undefined,
    refetchInterval: coIleMs,
    staleTime: 0,
    retry: false,
  });
  const odcisk = znacznik.data?.fingerprint;
  const ostatnio = useRef(0);
  const czeka = useRef<ReturnType<typeof setTimeout> | null>(null);
  const kluczTekst = JSON.stringify(klucz);
  useEffect(() => () => { if (czeka.current) clearTimeout(czeka.current); }, []);
  const znacznikPobranyO = znacznik.dataUpdatedAt;
  useEffect(() => {
    if (!odcisk || !odciskDanych || odcisk === odciskDanych || pobiera || czeka.current) return;
    // Dane widoku NOWSZE niż odczyt znacznika (zmiana w trakcie przeliczenia, recenzja #985 r4): nie przeliczamy
    // widoku ponownie — rozstrzyga NASTĘPNE zaplanowane odpytanie (≥10 s, po wygaśnięciu 5 s bufora odcisku na
    // serwerze). Natychmiastowe odświeżenie znacznika mogłoby dostać z bufora stary odcisk i zapętlić przeliczenia.
    if (danePobraneO > znacznikPobranyO) return;
    const odswiez = () => {
      czeka.current = null;
      ostatnio.current = Date.now();
      void kolejka.invalidateQueries({ queryKey: JSON.parse(kluczTekst) as unknown[], exact: true });
    };
    const zaIle = ostatnio.current + (blad ? Math.max(minOdstepMs, ODSTEP_PO_BLEDZIE_MS) : minOdstepMs) - Date.now();
    if (zaIle <= 0) odswiez();
    else czeka.current = setTimeout(odswiez, zaIle);
  }, [odcisk, odciskDanych, pobiera, blad, kolejka, kluczTekst, minOdstepMs, danePobraneO, znacznikPobranyO]);
  return {
    swiezosc: znacznik.data?.freshness,
    zgodne: Boolean(odcisk && odciskDanych && odcisk === odciskDanych),
    znacznikBlad: znacznik.isError,
  };
}

/**
 * Świeżość, którą pokazuje panel (recenzja #985 r3):
 * - odcisk znacznika = odcisk pokazanych danych → świeżość NA ŻYWO ze znacznika (panel nie zastyga);
 * - odcisk różny, a ponowny odczyt widoku PADŁ → STALE `VIEW_OUTDATED` — kwoty na ekranie są starsze niż replika;
 * - odcisk różny w czasie zwykłego oczekiwania (odstęp roku, chwila przed startem odczytu) → świeżość z odpowiedzi
 *   (bez fałszywego „przeliczenie się nie powiodło”, recenzja #985 r4);
 * - znacznik zawodzi → świeżość odpowiedzi + `FRESHNESS_UNKNOWN` (STALE) — nie zastygamy na ostatniej ocenie.
 */
function zeSwiezoscia<T extends { freshness: SwiezoscRozliczen }>(
  dane: T | undefined, z: { swiezosc: SwiezoscRozliczen | undefined; zgodne: boolean; znacznikBlad: boolean }, bladOdczytu: boolean,
): T | undefined {
  if (!dane || !dane.freshness) return dane;
  const dolozPowod = (powod: PowodNieswiezosci): T => ({
    ...dane,
    freshness: { ...dane.freshness, state: 'STALE', warnings: [...new Set([...dane.freshness.warnings, powod])] },
  });
  if (z.znacznikBlad) return dolozPowod('FRESHNESS_UNKNOWN');
  if (!z.swiezosc) return dane;
  if (z.zgodne) return { ...dane, freshness: z.swiezosc };
  return bladOdczytu ? dolozPowod('VIEW_OUTDATED') : dane;
}

export function useRozliczenia(tydzien?: { rokIso: number; tydzien: number }) {
  const api = useAnalyticsApi();
  const klucz = ['rozliczenia', tydzien?.rokIso ?? null, tydzien?.tydzien ?? null];
  const q = useQuery({
    queryKey: klucz,
    queryFn: () => api.rozliczenia(tydzien),
    ...QUERY_OPTIONS,
    staleTime: 0,
  });
  const p = q.data?.period;
  const zywa = useOdswiezPoZmianieDanych(klucz, p ? { year: p.isoYear, week: p.isoWeek } : null, q.data?.changeFingerprint,
    q.isFetching, q.isError, q.dataUpdatedAt);
  return { ...q, data: zeSwiezoscia(q.data, zywa, q.isError) };
}

/** Rozliczenie miesięczne (faktury TuSzamy). `staleTime: 0` jak tydzień — kwoty finansowe zawsze świeże. */
export function useRozliczeniaMiesiac(miesiac?: { rok: number; miesiac: number }) {
  const api = useAnalyticsApi();
  const klucz = ['rozliczenia-miesiac', miesiac?.rok ?? null, miesiac?.miesiac ?? null];
  const q = useQuery({
    queryKey: klucz,
    queryFn: () => api.rozliczeniaMiesiac(miesiac),
    ...QUERY_OPTIONS,
    staleTime: 0,
  });
  const p = q.data?.period;
  const zywa = useOdswiezPoZmianieDanych(klucz, p ? { widok: 'miesiac', year: p.year, month: p.month } : null,
    q.data?.changeFingerprint, q.isFetching, q.isError, q.dataUpdatedAt);
  return { ...q, data: zeSwiezoscia(q.data, zywa, q.isError) };
}

/** Rozliczenie roczne (rok rozliczeniowy IX..VIII). `staleTime: 0` — kwoty finansowe zawsze świeże. */
export function useRozliczeniaRok(start?: number) {
  const api = useAnalyticsApi();
  const klucz = ['rozliczenia-rok', start ?? null];
  const q = useQuery({
    queryKey: klucz,
    queryFn: () => api.rozliczeniaRok(start),
    ...QUERY_OPTIONS,
    staleTime: 0,
  });
  const p = q.data?.period;
  const zywa = useOdswiezPoZmianieDanych(klucz, p ? { widok: 'rok', start: p.startYear } : null, q.data?.changeFingerprint,
    q.isFetching, q.isError, q.dataUpdatedAt, ODSWIEZANIE_ROZLICZEN_MS.rokMinOdstep, ODSWIEZANIE_ROZLICZEN_MS.znacznikRok);
  return { ...q, data: zeSwiezoscia(q.data, zywa, q.isError) };
}

// ── ADMINISTRACJA → GRAFIK (kanoniczny) ───────────────────────────────────────

/**
 * Kanoniczny grafik. `staleTime: 0`: po zapisie albo zmianie w starym panelu powrót na ekran ma
 * pokazać stan systemu głównego, a nie minutę starą kopię.
 */
export function useGrafikKanoniczny() {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['grafik-kanoniczny'],
    queryFn: () => api.grafikKanoniczny(),
    ...QUERY_OPTIONS,
    staleTime: 0,
  });
}

/**
 * Wszystkie zapytania CC, które czytają relację restauracja ↔ szkoła z systemu głównego. Po zapisie
 * w Grafiku albo w Szkołach i restauracjach unieważniamy je RAZEM — ekran, na który operator przejdzie
 * zaraz potem, ma pokazać stan po zmianie, a nie kopię sprzed niej (do 2026-10-10 była tu też dawna
 * replika: stary Grafik jednej restauracji i Organizacja).
 */
export const ZAPYTANIA_GRAFIKU = [
  ['grafik-kanoniczny'],
  ['grafik-audyt'],
  ['admin-szkoly-restauracji'],
  ['admin-szkoly-restauracji-audyt'],
] as const;

export function odswiezEkranyGrafiku(queryClient: QueryClient) {
  return Promise.all(ZAPYTANIA_GRAFIKU.map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })));
}

export function useGrafikAudyt(wlaczone: boolean) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['grafik-audyt'],
    queryFn: () => api.grafikAudyt(),
    enabled: wlaczone,
    ...QUERY_OPTIONS,
    staleTime: 0,
  });
}

// ── ADMINISTRACJA → UŻYTKOWNICY ──────────────────────────────────────────────

/**
 * Strona listy kont. Filtry liczy serwer na PEŁNYM zbiorze; `placeholderData` trzyma poprzednią
 * stronę na ekranie podczas zmiany filtra (bez migotania pustej tabeli). `staleTime: 0` — saldo
 * po doładowaniu ma być widać od razu (serwer CC i tak trzyma pełną listę 60 s).
 */
export function useUzytkownicy(q: ZapytanieUzytkownikow) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['admin-uzytkownicy', q],
    queryFn: () => api.uzytkownicy(q),
    placeholderData: keepPreviousData,
    ...QUERY_OPTIONS,
    staleTime: 0,
  });
}

export function useAudytUzytkownikow(wlaczone: boolean) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['admin-uzytkownicy-audyt'],
    queryFn: () => api.uzytkownicyAudyt(),
    enabled: wlaczone,
    ...QUERY_OPTIONS,
    staleTime: 0,
  });
}

// ── ADMINISTRACJA → SZKOŁY I RESTAURACJE ─────────────────────────────────────

export function useAdminSzkolyRestauracji() {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['admin-szkoly-restauracji'],
    queryFn: () => api.adminSzkolyRestauracji(),
    ...QUERY_OPTIONS,
    staleTime: 0,
  });
}

export function useAudytSzkolRestauracji(wlaczone: boolean) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['admin-szkoly-restauracji-audyt'],
    queryFn: () => api.adminSzkolyRestauracjiAudyt(),
    enabled: wlaczone,
    ...QUERY_OPTIONS,
    staleTime: 0,
  });
}

// ── ADMINISTRACJA → MODERACJA MENU ───────────────────────────────────────────

/** Wszystkie zapytania Moderacji menu — po decyzji odświeżane RAZEM (liczniki zakładek też). */
export const ZAPYTANIA_MODERACJI = [
  ['moderacja-liczniki'],
  ['moderacja-kolejka'],
  ['moderacja-ai'],
  ['moderacja-historia'],
  ['moderacja-do-potwierdzenia'],
  ['moderacja-audyt'],
] as const;

export function odswiezModeracje(queryClient: QueryClient) {
  return Promise.all(ZAPYTANIA_MODERACJI.map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })));
}

const OPCJE_MODERACJI = { ...QUERY_OPTIONS, staleTime: 0, placeholderData: keepPreviousData } as const;

export function useLicznikiModeracji() {
  const api = useAnalyticsApi();
  return useQuery({ queryKey: ['moderacja-liczniki'], queryFn: () => api.moderacjaLiczniki(), ...QUERY_OPTIONS, staleTime: 0 });
}

export function useKolejkaModeracji(strona: number, wlaczone = true) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['moderacja-kolejka', strona],
    queryFn: () => api.moderacjaKolejka({ strona, rozmiar: 20 }),
    enabled: wlaczone,
    ...OPCJE_MODERACJI,
  });
}

export function useZatwierdzonePrzezAi(strona: number, wlaczone = true) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['moderacja-ai', strona],
    queryFn: () => api.moderacjaZatwierdzonePrzezAi({ strona, rozmiar: 20 }),
    enabled: wlaczone,
    ...OPCJE_MODERACJI,
  });
}

export function useHistoriaModeracji(strona: number, filtr: FiltrHistoriiModeracji, wlaczone = true) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['moderacja-historia', filtr, strona],
    queryFn: () => api.moderacjaHistoria({ strona, rozmiar: 20, filtr }),
    enabled: wlaczone,
    ...OPCJE_MODERACJI,
  });
}

export function useDaniaDoPotwierdzenia(wlaczone = true) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['moderacja-do-potwierdzenia'],
    queryFn: () => api.moderacjaDoPotwierdzenia(),
    enabled: wlaczone,
    ...QUERY_OPTIONS,
    staleTime: 0,
  });
}

export function useAudytModeracji(wlaczone: boolean) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['moderacja-audyt'],
    queryFn: () => api.moderacjaAudyt(),
    enabled: wlaczone,
    ...QUERY_OPTIONS,
    staleTime: 0,
  });
}

export function useOpinie(q: OpinieZapytanie = {}) {
  const api = useAnalyticsApi();
  return useQuery({ queryKey: ['opinie', q], queryFn: () => api.opinie(q), ...QUERY_OPTIONS });
}

// ── PANEL RESTAURACJI ────────────────────────────────────────────────────────

export function useDzisiejszeZamowienia(restauracja?: number, dzien?: string) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['restauracja-dzisiaj', restauracja ?? null, dzien ?? null],
    queryFn: () => api.dzisiejszeZamowienia(restauracja, dzien),
    /*
     * Ekran jest używany W TRAKCIE przygotowania posiłków. Dane, które cicho się starzeją,
     * są tu gorsze niż brak danych — kucharz ugotuje wtedy według stanu sprzed godziny.
     * Odświeżamy więc co minutę i przy powrocie do okna.
     */
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    ...QUERY_OPTIONS,
  });
}

export function useSzkolyRestauracji(restauracja?: number) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['restauracja-szkoly', restauracja ?? null],
    queryFn: () => api.szkolyRestauracji(restauracja),
    ...QUERY_OPTIONS,
  });
}

/**
 * ZDOLNOŚCI ŚRODOWISKA — co naprawdę działa w tym wdrożeniu.
 *
 * Trzymane dłużej niż zwykłe dane: to nie są liczby, które zmieniają się w trakcie pracy,
 * tylko kształt środowiska. Odpytywanie o to przy każdym ekranie byłoby hałasem.
 */
export function useZdolnosci() {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['zdolnosci'],
    queryFn: () => api.zdolnosci(),
    ...QUERY_OPTIONS,
    // PO rozwinięciu wspólnych opcji — inaczej `QUERY_OPTIONS` nadpisałoby tę wartość
    // i zdolności odpytywane byłyby tak często jak zwykłe dane. To nie są liczby zmieniające
    // się w trakcie pracy, tylko kształt środowiska.
    staleTime: 5 * 60_000,
  });
}


export function useMenuDnia(data: string, restauracja?: number) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['restauracja-menu-dnia', data, restauracja ?? null],
    queryFn: () => api.menuDnia(data, restauracja),
    ...QUERY_OPTIONS,
  });
}

export function useDaniaRestauracji(restauracja?: number) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['restauracja-dania', restauracja ?? null],
    queryFn: () => api.daniaRestauracji(restauracja),
    ...QUERY_OPTIONS,
  });
}

/**
 * Historia transakcji restauracji. Stronicowanie po stronie serwera — `keepPreviousData` sprawia,
 * że przy przejściu na kolejną stronę tabela nie miga pustką, tylko przyciemnia poprzednie wiersze.
 */
export function useTransakcjeRestauracji(zapytanie: ZapytanieTransakcjiRestauracji = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: [
      'restauracja-transakcje',
      zapytanie.restauracja ?? null,
      zapytanie.od ?? null,
      zapytanie.do ?? null,
      zapytanie.status ?? null,
      zapytanie.skasowane ?? null,
      zapytanie.strona ?? 1,
    ],
    queryFn: () => api.transakcjeRestauracji(zapytanie),
    placeholderData: (poprzednie) => poprzednie,
    ...QUERY_OPTIONS,
  });
}

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  RAPORTY DOSTAW — jedno zapytanie na sekcję, wspólny klucz okresu
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Klucz zapytania zawiera KOMPLET filtrów. Pominięcie któregokolwiek dawałoby wynik z pamięci
 * podręcznej dla innego zestawu — czyli tabelę spóźnień jednego kierowcy podpisaną nazwiskiem
 * drugiego. W raporcie, na którym opiera się rozmowa z kierowcą, jest to najgorszy możliwy błąd.
 */
function kluczRaportu(nazwa: string, z: ZapytanieRaportuDostaw) {
  return [
    nazwa, z.restauracja ?? null, z.od ?? null, z.do ?? null,
    z.kierowca ?? null, z.szkola ?? null, z.status ?? null,
    z.punktualnosc ?? null, z.zgloszenie ?? null, z.trafnosc ?? null,
    z.strona ?? 1, z.rozmiar ?? null,
  ];
}

export function usePodsumowanieDostaw(z: ZapytanieRaportuDostaw = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: kluczRaportu('dostawy-podsumowanie', z),
    queryFn: () => api.raportDostawPodsumowanie(z),
    ...QUERY_OPTIONS,
  });
}

export function useKierowcyDostaw(z: ZapytanieRaportuDostaw = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: kluczRaportu('dostawy-kierowcy', z),
    queryFn: () => api.raportDostawKierowcy(z),
    ...QUERY_OPTIONS,
  });
}

export function useSzkolyDostaw(z: ZapytanieRaportuDostaw = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: kluczRaportu('dostawy-szkoly', z),
    queryFn: () => api.raportDostawSzkoly(z),
    ...QUERY_OPTIONS,
  });
}

export function useDniDostaw(z: ZapytanieRaportuDostaw = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: kluczRaportu('dostawy-dni', z),
    queryFn: () => api.raportDostawDni(z),
    ...QUERY_OPTIONS,
  });
}

export function useListaDostaw(z: ZapytanieRaportuDostaw = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: kluczRaportu('dostawy-lista', z),
    queryFn: () => api.raportDostawLista(z),
    placeholderData: (poprzednie) => poprzednie,
    ...QUERY_OPTIONS,
  });
}

export function useFiltryDostaw(z: ZapytanieRaportuDostaw = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    // Opcje filtrów zależą WYŁĄCZNIE od okresu i restauracji — nie od wybranych filtrów.
    // Inaczej wybranie kierowcy skasowałoby z listy pozostałych i nie dałoby się go zmienić.
    queryKey: ['dostawy-filtry', z.restauracja ?? null, z.od ?? null, z.do ?? null],
    queryFn: () => api.raportDostawFiltry({ restauracja: z.restauracja, od: z.od, do: z.do }),
    ...QUERY_OPTIONS,
  });
}
